/**************************************************
 * /src/components/form/Input.js
 *
 * @author  문영일
 * @since   1.0 (2025-08-20)
 * @version 1.0
 *
 * @description
 * 공용 Input 컴포넌트.
 * - react-hook-form의 Controller / register 방식 모두 지원
 * - [추가] onClick 전달 시 disabled를 대체하여 클릭 가능 처리:
 *   · disabled → readOnly 전환
 *   · aria-disabled 부여 (접근성)
 *   · 포인터 커서 적용
 *   · Enter/Space 키로 onClick 트리거
 **************************************************/
import React from "react";
import { Controller, useFormContext } from "react-hook-form";

const Input = ({
                   label,
                   name,
                   type = "text",
                   placeholder,
                   divStyle,
                   className = "",
                   maxLength,
                   control,
                   rules = {},
                   ...rest
               }) => {
    const { register } = useFormContext();

    const {
        onClick,
        disabled: disabledProp,
        readOnly: readOnlyProp,
        style: styleProp,
        ...restProps
    } = rest;

    const clickable = typeof onClick === "function";
    const passDisabled = disabledProp && !clickable;
    const effectiveReadOnly = clickable ? true : !!readOnlyProp;
    const effectiveStyle = clickable
        ? { cursor: "pointer", ...styleProp }
        : styleProp;

    // 키보드 접근성: Enter/Space로 onClick 트리거
    const handleKeyDown = (e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.(e);
        }
    };

    const commonProps = {
        id: name,
        name,
        type,
        placeholder,
        className,
        maxLength,
        onClick,
        onKeyDown: clickable ? handleKeyDown : undefined,
        readOnly: effectiveReadOnly,
        "aria-disabled": disabledProp || undefined,
        disabled: passDisabled,
        style: effectiveStyle,
        ...restProps,
    };

    return (
        <div style={divStyle}>
            {label && (
                <label htmlFor={name} className="input-label">
                    {label}
                </label>
            )}

            {control ? (
                <Controller
                    name={name}
                    control={control}
                    rules={rules}
                    render={({ field }) => (
                        <input
                            {...field}
                            {...commonProps}
                        />
                    )}
                />
            ) : (
                <input
                    {...register(name, rules)}
                    {...commonProps}
                />
            )}
        </div>
    );
};

export default Input;